<script setup>
import { computed, ref, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import ConversationApi from 'dashboard/api/conversations';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const [showTransferMenu, toggleTransferMenu] = useToggle(false);
const [showTransferAgents, toggleTransferAgents] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);

const agents = computed(() => store.getters['agents/getAgents'] || []);

const transferAgentMenuItems = computed(() => {
  return agents.value.map(agent => ({
    icon: 'i-lucide-user cw-opt-blue',
    label: agent.name || agent.email || `Agente ${agent.id}`,
    value: `agent_${agent.id}`,
  }));
});

const transferInboxMenuItems = computed(() => {
  const inboxes = store.getters['inboxes/getInboxes'] || [];
  return inboxes
    .filter(inbox => inbox.id !== currentChat.value.inbox_id)
    .map(inbox => ({
      icon: 'i-lucide-inbox cw-opt-purple',
      label: inbox.name || `Caixa ${inbox.id}`,
      value: `inbox_${inbox.id}`,
    }));
});

const actionMenuItems = computed(() => {
  const items = [];

  if (currentChat.value.status !== 'resolved') {
    items.push({
      icon: 'i-lucide-check-circle cw-opt-teal',
      label: 'Marcar como resolvida',
      action: 'status_resolved',
      value: 'status_resolved',
    });
  }

  if (currentChat.value.status !== 'pending') {
    items.push({
      icon: 'i-lucide-hourglass cw-opt-ruby',
      label: 'Marcar como pendente',
      action: 'status_pending',
      value: 'status_pending',
    });
  }

  if (currentChat.value.status !== 'open') {
    items.push({
      icon: 'i-lucide-mail cw-opt-blue',
      label: 'Marcar como não lida',
      action: 'status_open',
      value: 'status_open',
    });
  }

  items.push({
    icon: 'i-lucide-alarm-clock cw-opt-amber',
    label: 'Adiar por 24h',
    action: 'snooze_24h',
    value: 'snooze_24h',
  });

  items.push({
    icon: 'i-lucide-flag cw-opt-amber',
    label: 'Prioridade: baixa',
    action: 'priority_low',
    value: 'priority_low',
  });
  items.push({
    icon: 'i-lucide-flag cw-opt-amber',
    label: 'Prioridade: média',
    action: 'priority_medium',
    value: 'priority_medium',
  });
  items.push({
    icon: 'i-lucide-flag cw-opt-amber',
    label: 'Prioridade: alta',
    action: 'priority_high',
    value: 'priority_high',
  });

  items.push({
    icon: 'i-lucide-share cw-opt-blue',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-external-link cw-opt-blue',
    label: 'Open in new tab',
    action: 'open_new_tab',
    value: 'open_new_tab',
  });

  items.push({
    icon: 'i-lucide-link cw-opt-purple',
    label: 'Copiar link da conversa',
    action: 'copy_link',
    value: 'copy_link',
  });

  if (agents.value.length) {
    items.push({
      icon: 'i-lucide-user cw-opt-blue',
      label: 'Transferir atendimento',
      action: 'show_agents',
      value: 'show_agents',
    });
  }
  if (transferInboxMenuItems.value.length) {
    items.push({
      icon: 'i-lucide-inbox cw-opt-purple',
      label: 'Transferir canal',
      action: 'show_inboxes',
      value: 'show_inboxes',
    });
  }

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'status_resolved') {
    store.dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status: 'resolved',
    });
  } else if (action === 'status_pending') {
    store.dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status: 'pending',
    });
  } else if (action === 'status_open') {
    store.dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status: 'open',
    });
  } else if (action === 'snooze_24h') {
    const snoozedUntil = new Date(Date.now() + 24 * 60 * 60 * 1000);
    store.dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status: 'snoozed',
      snoozedUntil: snoozedUntil.toISOString(),
    });
  } else if (action.startsWith('priority_')) {
    const priorityMap = {
      priority_low: 'low',
      priority_medium: 'medium',
      priority_high: 'high',
    };
    store.dispatch('assignPriority', {
      conversationId: currentChat.value.id,
      priority: priorityMap[action],
    });
  } else if (action === 'open_new_tab') {
    const url = `${window.chatwootConfig.hostURL}${frontendURL(
      conversationUrl({
        id: currentChat.value.id,
        accountId: store.getters.getCurrentAccountId,
      })
    )}`;
    window.open(url, '_blank');
  } else if (action === 'copy_link') {
    const url = `${window.chatwootConfig.hostURL}${frontendURL(
      conversationUrl({
        id: currentChat.value.id,
        accountId: store.getters.getCurrentAccountId,
      })
    )}`;
    navigator.clipboard?.writeText(url);
    useAlert('Link copiado');
  } else if (action === 'show_agents') {
    toggleTransferAgents(true);
    toggleTransferMenu(true);
  } else if (action === 'show_inboxes') {
    toggleTransferAgents(false);
    toggleTransferMenu(true);
  }
};

const handleTransferAction = ({ value }) => {
  toggleTransferMenu(false);
  toggleTransferAgents(false);

  if (value.startsWith('agent_')) {
    const agentId = Number(value.replace('agent_', ''));
    store
      .dispatch('assignAgent', {
        conversationId: currentChat.value.id,
        agentId,
        assigneeType: 'Agent',
      })
      .then(() => {
        useAlert('Atendimento transferido.');
      })
      .catch(() => {
        useAlert('Falha ao transferir o atendimento.');
      });
  } else if (value.startsWith('inbox_')) {
    const inboxId = Number(value.replace('inbox_', ''));
    ConversationApi.moveConversation(currentChat.value.id, inboxId)
      .then(() => {
        useAlert('Canal transferido.');
      })
      .catch(() => {
        useAlert('Falha ao transferir o canal.');
      });
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => {
        toggleDropdown(false);
        toggleTransferMenu(false);
        toggleTransferAgents(false);
      }"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
      <DropdownMenu
        v-if="showTransferMenu"
        :menu-items="
          showTransferAgents
            ? transferAgentMenuItems
            : transferInboxMenuItems
        "
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleTransferAction"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>

<style>
.cw-opt-teal { color: #0d9488; }
.cw-opt-ruby { color: #e11d48; }
.cw-opt-blue { color: #2563eb; }
.cw-opt-amber { color: #d97706; }
.cw-opt-purple { color: #7c3aed; }
</style>
